import { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

export default function SuccessStories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const brandColor = '#e46809';

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await fetch(`/api/stories`);
        if (!response.ok) throw new Error("Could not load success stories at this time.");
        const data = await response.json();
        setStories(data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  // Don't render the section at all if there's an error or no stories
  if (loading || error || !stories || stories.length === 0) {
    return null; 
  }

  const handleNext = () => setCurrentIndex((prevIndex) => (prevIndex + 1) % stories.length);
  const handlePrevious = () => setCurrentIndex((prevIndex) => (prevIndex - 1 + stories.length) % stories.length);
  
  const currentStory = stories[currentIndex];

  return (
    <section id="success-stories" className="bg-gray-50 dark:bg-gray-900 py-20">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Success Stories
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            See how your donations empower individuals with independence and gratitude.
          </p>
          <div className="w-24 h-1 mx-auto mt-6" style={{ backgroundColor: brandColor }} />
        </div>

        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="grid md:grid-cols-2 gap-8 items-center" aria-live="polite">
            <div className="text-gray-800 dark:text-gray-200 order-2 md:order-1">
              <p className="text-lg mb-2">
                <span className="font-semibold text-gray-500 dark:text-gray-400 block text-sm">Recipient</span> 
                {currentStory.recipient_name}
              </p>
              <h3 className="text-2xl font-bold mt-2 text-gray-900 dark:text-white">{currentStory.title}</h3>
              <blockquote className="italic text-gray-600 dark:text-gray-400 mt-4 border-l-4 border-orange-500 pl-4 py-2">
                “{currentStory.story_text}”
              </blockquote>
            </div>
            
            <div className="flex justify-center md:justify-end order-1 md:order-2">
               <img 
                key={currentStory.id}
                src={currentStory.image_url} 
                alt={currentStory.title}
                className="rounded-xl w-full max-w-sm h-auto object-cover shadow-lg"
              />
            </div>
          </div>
          
          <div className="w-full h-px bg-gray-200 dark:bg-gray-700 mt-8 mb-6" />

          <div className="flex justify-between items-center font-semibold text-gray-700 dark:text-gray-300">
              <button onClick={handlePrevious} className="flex items-center hover:text-orange-500 transition-colors disabled:opacity-50" disabled={stories.length <= 1}>
                  <ChevronLeftIcon className="w-5 h-5 mr-1" /> PREV
              </button>
              <span className="text-sm font-mono">{currentIndex + 1} / {stories.length}</span>
              <button onClick={handleNext} className="flex items-center hover:text-orange-500 transition-colors" disabled={stories.length <= 1}>
                  NEXT <ChevronRightIcon className="w-5 h-5 ml-1" />
              </button>
          </div>
        </div>
      </div>
    </section>
  );
}